import { isObservable, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { CreateErrorBase } from '../models';

export type BaseParams<T = object> = {
  [key: string]: any;
} & T;

export interface CommandModel<T extends BaseParams = object> {
  execute: (params?: T) => void;
}

export interface CommandConfig<
  T extends BaseParams = object,
  R extends BaseParams = object
> {
  params?: () => T;
  onStart?: () => void;
  onEnd?: () => void;
  onSuccess?: (result: R) => void;
  onFailed?: (err: CreateErrorBase) => void;
}

export abstract class BaseCommand<
  T extends BaseParams = object,
  R extends BaseParams = object
> {
  public build(config: CommandConfig<T, R>): CommandModel<T> {
    return {
      execute: (params?: T) => {
        config.onStart?.();
        const result = this.execute({ ...(config.params?.() as T), ...params });
        if (isObservable(result)) {
          result.pipe(take(1)).subscribe(
            (res) => {
              config.onEnd?.();
              config.onSuccess?.({
                ...res,
                entryParams: { ...params },
              } as R);
            },
            (err) => config.onFailed?.(err),
          );
        } else {
          config.onSuccess?.(result as R);
        }
      },
    };
  }

  public abstract execute(params: T): void | R | Observable<R>;
}
